import { isApiError } from "@core/api/api-error";
import { useI18n } from "@core/i18n/i18n";
import { useQueryParams } from "@core/routing/use-query-params";
import { useRouteParams } from "@core/routing/use-route-params";
import { usePatient } from "@domain/patient/hooks/use-patients";
import { getEmptyNewProgram } from "@domain/program/program";

import styled from "@emotion/styled";
import { WordingKey } from "@typings/i18n-typings";
import { NetworkError } from "@ui/components/common/error";
import { RoutesPath } from "@ui/navigation/routes";
import { LoadingEmptyScreen, LoadingSize } from "@ui/pages/login/loading-empty-screen";
import { usePatientProgram } from "@ui/pages/program-builder/hooks/use-patient-program";
import { ProgramBuilderView } from "@ui/pages/program-builder/program-builder-view";
import { useTemplate } from "@ui/pages/template-builder/hooks/use-templates";
import { stack } from "@ui/style";
import { stringToBoolean } from "@utils/string-utils";
import React, { useMemo } from "react";

export const ProgramBuilderPage: React.FC = () => {
	const { patientId } = useRouteParams<RoutesPath.ProgramBuilder>();
	const { programId, templateId, openScheduleModal } = useQueryParams<RoutesPath.ProgramBuilder>();
	const { format } = useI18n();

	if (!patientId) {
		return (
			<Container>
				<NetworkError description={format("global.patientNotFound.description")} />
			</Container>
		);
	}

	return (
		<ProgramBuilderContent
			patientId={patientId}
			programId={programId}
			templateId={templateId}
			openScheduleModal={stringToBoolean(openScheduleModal ?? "")}
		/>
	);
};

const ProgramBuilderContent: React.FC<{
	patientId: string;
	programId?: string;
	templateId?: string;
	openScheduleModal?: boolean;
}> = ({ patientId, programId, templateId, openScheduleModal }) => {
	const { format } = useI18n();
	const { loading: patientLoading, patient, error: errorPatient, refreshPatient } = usePatient(patientId);
	const {
		loading: programLoading,
		program,
		error: errorProgram,
		refreshProgram,
	} = usePatientProgram({ programId, patientId });
	const { template, loading: templateLoading, error: errorTemplate, refreshTemplate } = useTemplate(templateId);

	const currentProgram = useMemo(
		() => (programId ? program : getEmptyNewProgram(patientId)),
		[patientId, program, programId],
	);

	if (!currentProgram || !patient || (templateId && !template)) {
		if (patientLoading || programLoading || templateLoading) {
			return <LoadingEmptyScreen size={LoadingSize.FullSpace} />;
		}
		return (
			<Container>
				<NetworkError
					description={getErrorWording(format, errorPatient, errorProgram, errorTemplate)}
					onRefresh={async () => {
						if (patientId && programId) {
							await refreshPatient(patientId);
							await refreshProgram(patientId, programId);
							if (templateId) {
								await refreshTemplate(templateId);
							}
						}
					}}
				/>
			</Container>
		);
	}

	return (
		<ProgramBuilderView
			patient={patient}
			program={currentProgram}
			programIsLoading={programLoading || templateLoading}
			template={template}
			openScheduleModal={openScheduleModal}
		/>
	);
};

function getErrorWording(
	format: (key: WordingKey) => string,
	patientError: unknown,
	programError: unknown,
	templateError: unknown,
): string | undefined {
	if (isApiError(patientError)) {
		return format("global.patientNotFound.description");
	}

	if (isApiError(programError)) {
		return format("global.programNotFound.description");
	}

	if (isApiError(templateError)) {
		return format("templateBuilder.templateNotFoundError");
	}

	return undefined;
}

const Container = styled.div`
	${stack(undefined, "center", "stretch")};
	flex-grow: 1;
`;
